import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Mail, ChevronRight, Check, Loader2, Sparkles } from "lucide-react";
import BottomSheet from "./BottomSheet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const T = {
  it: {
    title: "Rimani in contatto",
    desc: "Lascia la tua mail per ricevere offerte, promo e tanto altro.",
    sheetSub: "Solo l'host ti scriverà, quando c'è qualcosa di buono.",
    emailPh: "La tua email",
    consent: "Acconsento all'uso della mia email solo da parte dell'host per inviarmi messaggi su promozioni, sconti, novità o offerte dedicate alla struttura.",
    submit: "Iscrivimi",
    sending: "Invio…",
    okTitle: "Sei dei nostri! 🎉",
    okDesc: "Ti scriveremo solo quando ci saranno offerte e novità che valgono davvero.",
    close: "Chiudi",
    errEmail: "Inserisci un'email valida",
    errConsent: "Devi accettare per continuare",
    errGeneric: "Qualcosa è andato storto, riprova.",
  },
  en: {
    title: "Stay in touch",
    desc: "Leave your email to get offers, promos and much more.",
    sheetSub: "Only the host will write to you, when there's something good.",
    emailPh: "Your email",
    consent: "I agree that my email will be used only by the host to send me messages about promotions, discounts, news or offers dedicated to the property.",
    submit: "Sign me up",
    sending: "Sending…",
    okTitle: "You're in! 🎉",
    okDesc: "We'll only write when there are offers and news truly worth it.",
    close: "Close",
    errEmail: "Please enter a valid email",
    errConsent: "You must accept to continue",
    errGeneric: "Something went wrong, please retry.",
  },
  fr: {
    title: "Restons en contact",
    desc: "Laissez votre email pour recevoir offres, promos et bien plus.",
    sheetSub: "Seul l'hôte vous écrira, quand il y a du bon.",
    emailPh: "Votre email",
    consent: "J'accepte que mon email soit utilisé uniquement par l'hôte pour m'envoyer des messages sur les promotions, réductions, nouveautés ou offres dédiées à la structure.",
    submit: "Je m'inscris",
    sending: "Envoi…",
    okTitle: "Vous en êtes ! 🎉",
    okDesc: "Nous vous écrirons seulement pour des offres et nouveautés qui en valent la peine.",
    close: "Fermer",
    errEmail: "Saisissez un email valide",
    errConsent: "Vous devez accepter pour continuer",
    errGeneric: "Une erreur est survenue, réessayez.",
  },
  es: {
    title: "Sigamos en contacto",
    desc: "Deja tu email para recibir ofertas, promos y mucho más.",
    sheetSub: "Solo el anfitrión te escribirá, cuando haya algo bueno.",
    emailPh: "Tu email",
    consent: "Acepto que mi email sea usado únicamente por el anfitrión para enviarme mensajes sobre promociones, descuentos, novedades u ofertas dedicadas a la estructura.",
    submit: "Suscribirme",
    sending: "Enviando…",
    okTitle: "¡Ya estás dentro! 🎉",
    okDesc: "Solo te escribiremos cuando haya ofertas y novedades que valgan la pena.",
    close: "Cerrar",
    errEmail: "Introduce un email válido",
    errConsent: "Debes aceptar para continuar",
    errGeneric: "Algo salió mal, inténtalo de nuevo.",
  },
  de: {
    title: "In Kontakt bleiben",
    desc: "Hinterlasse deine E-Mail für Angebote, Aktionen und mehr.",
    sheetSub: "Nur der Gastgeber schreibt dir, wenn es etwas Gutes gibt.",
    emailPh: "Deine E-Mail",
    consent: "Ich stimme zu, dass meine E-Mail nur vom Gastgeber verwendet wird, um mir Nachrichten über Aktionen, Rabatte, Neuigkeiten oder Angebote der Unterkunft zu senden.",
    submit: "Anmelden",
    sending: "Senden…",
    okTitle: "Du bist dabei! 🎉",
    okDesc: "Wir schreiben nur, wenn es wirklich lohnende Angebote und Neuigkeiten gibt.",
    close: "Schließen",
    errEmail: "Bitte gib eine gültige E-Mail ein",
    errConsent: "Du musst zustimmen, um fortzufahren",
    errGeneric: "Etwas ist schiefgelaufen, bitte erneut versuchen.",
  },
};

export default function StayInTouch({ propertyId, lang = "it", large = false }) {
  const t = T[lang] || T.it;
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const valid = EMAIL_RE.test(email.trim()) && consent;

  const submit = async () => {
    setError("");
    if (!EMAIL_RE.test(email.trim())) { setError(t.errEmail); return; }
    if (!consent) { setError(t.errConsent); return; }
    setSending(true);
    try {
      await axios.post(`${API}/contacts`, {
        property_id: propertyId,
        email: email.trim(),
        consent: true,
        lang,
      });
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail === "Invalid email" ? t.errEmail : t.errGeneric);
    } finally {
      setSending(false);
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => { setDone(false); setEmail(""); setConsent(false); setError(""); }, 300);
  };

  return (
    <>
      <motion.button
        variants={undefined}
        onClick={() => setOpen(true)}
        whileTap={{ scale: 0.97 }}
        className={`w-full ${large ? "rounded-[24px]" : "mt-2 rounded-2xl"} overflow-hidden text-left`}
        style={{ background: "#2F5BD9" }}
        data-testid="stay-in-touch-btn"
      >
        <div className={`flex items-center gap-3 text-white ${large ? "px-4 py-4" : "px-4 py-3"}`}>
          <div className="flex-1 min-w-0">
            <p className={`disp font-extrabold leading-tight ${large ? "text-[24px]" : "text-[13px] truncate"}`}>{t.title}</p>
            <p className={`text-white/85 font-medium leading-snug line-clamp-2 ${large ? "text-[13.5px] mt-1" : "text-[10px]"}`}>{t.desc}</p>
          </div>
          <div className={`rounded-full flex items-center justify-center shrink-0 ${large ? "w-[46px] h-[46px]" : "w-10 h-10"}`} style={{ border: "1.5px solid rgba(255,255,255,.55)" }}>
            <Mail className={large ? "w-[22px] h-[22px]" : "w-5 h-5"} strokeWidth={1.8} />
          </div>
        </div>
      </motion.button>

      <BottomSheet
        open={open}
        onClose={close}
        title={t.title}
        subtitle={t.sheetSub}
        accent="#2563EB"
        icon={Mail}
        testId="stay-in-touch-sheet"
      >
        {done ? (
          <div className="py-6 text-center" data-testid="stay-in-touch-success">
            <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-blue-600" strokeWidth={2.6} />
            </div>
            <p className="text-lg font-black text-zinc-900">{t.okTitle}</p>
            <p className="text-[13px] text-zinc-500 mt-1.5 max-w-sm mx-auto leading-relaxed">{t.okDesc}</p>
            <button
              onClick={close}
              className="mt-6 px-6 py-3 rounded-xl bg-zinc-900 text-white font-bold text-sm"
              data-testid="stay-in-touch-close-success"
            >
              {t.close}
            </button>
          </div>
        ) : (
          <div className="pt-1 pb-2">
            <div className="flex items-center gap-2 mb-3 text-blue-600">
              <Sparkles className="w-4 h-4" />
              <span className="text-[12px] font-bold">{t.desc}</span>
            </div>

            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(""); }}
              placeholder={t.emailPh}
              className="w-full px-4 py-3.5 rounded-2xl border border-zinc-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-[15px] font-medium text-zinc-900 placeholder:text-zinc-400 transition"
              data-testid="stay-in-touch-email"
            />

            <label className="flex items-start gap-3 mt-4 cursor-pointer select-none" data-testid="stay-in-touch-consent-label">
              <span
                onClick={(e) => { e.preventDefault(); setConsent(!consent); setError(""); }}
                className={`mt-0.5 w-6 h-6 rounded-lg flex items-center justify-center shrink-0 border-2 transition-colors ${consent ? "bg-blue-600 border-blue-600" : "bg-white border-zinc-300"}`}
                data-testid="stay-in-touch-consent"
                role="checkbox"
                aria-checked={consent}
              >
                {consent && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
              </span>
              <span className="text-[12px] text-zinc-500 leading-relaxed">{t.consent}</span>
            </label>

            {error && <p className="text-[12px] text-red-500 font-semibold mt-3" data-testid="stay-in-touch-error">{error}</p>}

            <button
              onClick={submit}
              disabled={!valid || sending}
              className="disp w-full mt-5 py-3.5 rounded-2xl font-extrabold text-[16px] flex items-center justify-center gap-2 transition-opacity disabled:opacity-40"
              style={{ background: "#FFC700", color: "#1A0B2E" }}
              data-testid="stay-in-touch-submit"
            >
              {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> {t.sending}</> : t.submit}
            </button>
          </div>
        )}
      </BottomSheet>
    </>
  );
}
